import { getStorageKey, setStorageKey } from "./storage";

export const DEVICE_ID_KEY = "deviceId";
export const REFRESH_TOKEN_KEY = "refreshToken";

const randomId = () => {
  try {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (error) {
    // Fall through to the Math.random path below.
  }

  // Fallback for browsers without crypto.randomUUID (older Safari). This is a
  // device *label*, never a secret — the refresh token is the credential — so
  // Math.random() is acceptable here.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * Stable device id for this browser profile, minted once and kept in
 * localStorage.
 *
 * The backend ties every refresh token to the `device_id` it was issued
 * against (`RCUserAuthenticationService::refreshToken()` matches on both), so
 * the id has to survive a reload — otherwise the stored refresh token can
 * never be redeemed and the session dies at the first 401. The previous
 * `"rc-webapp-" + Date.now()` was fresh on every sign-in, which made refresh
 * structurally impossible.
 *
 * Scope follows localStorage, which is exactly the scope we want:
 *   - same browser profile  → same id, session persists
 *   - incognito window      → separate store, no id, sign-in required
 *   - explicit log out      → clearStorage() drops it, sign-in required
 */
export const getDeviceId = () => {
  const existing = getStorageKey(DEVICE_ID_KEY);

  if (existing) {
    return existing;
  }

  const deviceId = `rc-webapp-${randomId()}`;

  // Best-effort persist — setStorageKey swallows quota/disabled-storage
  // errors. If it didn't stick we still return a usable id so sign-in works;
  // that session just won't be refreshable.
  setStorageKey(DEVICE_ID_KEY, deviceId);

  return deviceId;
};
